import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { login } from '@/services/api';
import { useAuthStore, useT } from '@/store';
import { LanguageToggle } from '@/components/layout/LanguageToggle';
import { FleetKineticIcon } from '@/components/brand/FleetKineticMark';

/**
 * Blocking login panel. Rendered instead of the app — not over it — so no
 * query can fire against a missing key and no half-rendered console shows
 * behind a modal.
 *
 * The monolith used an inline panel too, for a reason worth preserving: a
 * native prompt() is blocked or auto-dismissed in several embedded and
 * automated browser contexts this gets demoed in, which made the app look
 * broken rather than locked.
 */
export function AuthGate() {
  const t = useT();
  const signIn = useAuthStore((s) => s.signIn);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const mutation = useMutation({
    mutationFn: () => login(username.trim(), password),
    onSuccess: (body) => {
      setPassword('');
      // A driver signing in here is a wrong-door problem, not a failed
      // login: their key is scoped to their own order, so every panel in
      // this console would 403. Send them to the app that is actually built
      // for them instead of letting them in to a screen of errors. The
      // session is deliberately not stored first — the Driver App keeps its
      // own (see lib/appScope.js) and will ask them to sign in there.
      if (body.role === 'driver') {
        window.location.assign('/driver.html');
        return;
      }
      signIn(body);
    },
  });

  const submit = (event) => {
    event.preventDefault();
    if (!username.trim() || !password) return;
    mutation.mutate();
  };

  // A network failure and a rejected credential are different messages: one
  // says "the backend is not running", the other "check your password".
  // `login()` only throws an ApiError with a status when it got a response,
  // so the absence of one means the fetch itself failed.
  // A suspended or cancelled subscription is a third case, and it is the one
  // this line used to get most wrong: the backend answers 403 with its own
  // code, and rendering that as "invalid username or password" sends an
  // owner hunting for a password problem they do not have.
  let errorText = '';
  if (mutation.isError) {
    if (mutation.error?.body?.code === 'err.tenant_not_active_admin') {
      errorText = t('auth.accountInactive');
    } else {
      errorText = mutation.error?.status ? t('auth.invalidCreds') : t('auth.serverUnreachable');
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center p-4">
      <form onSubmit={submit} className="panel flex w-full max-w-[380px] flex-col !p-6">
        <div className="mb-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <FleetKineticIcon size={22} />
            <span className="font-brand text-[13.5px] font-bold">FleetKinetic</span>
          </div>
          <LanguageToggle />
        </div>

        <h1 className="font-brand text-[17px] font-bold">{t('auth.title')}</h1>
        <p className="mb-4 mt-1 text-[11.5px] leading-relaxed text-dim">{t('auth.subtitle')}</p>

        <label className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim" htmlFor="auth-username">
          {t('auth.usernameLabel')}
        </label>
        <input
          id="auth-username"
          className="field mb-3.5"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder={t('auth.usernamePh')}
          autoComplete="username"
          autoFocus
        />

        <label className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim" htmlFor="auth-password">
          {t('auth.passwordLabel')}
        </label>
        <div className="relative">
          <input
            id="auth-password"
            className="field pr-10"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('auth.passwordPh')}
            autoComplete="current-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            title={t(showPassword ? 'auth.hidePassword' : 'auth.showPassword')}
            className="absolute right-1 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-dim hover:bg-white/6 hover:text-ink"
          >
            {showPassword ? '🙈' : '👁'}
          </button>
        </div>

        <button
          type="submit"
          disabled={mutation.isPending || !username.trim() || !password}
          className="btn btn-primary mt-3.5 w-full justify-center py-2.5 text-[13px] disabled:opacity-65"
        >
          {t(mutation.isPending ? 'auth.loginBtnLoading' : 'auth.loginBtn')}
        </button>

        <p className="mt-2.5 min-h-[14px] text-[11.5px] leading-snug text-red" role="alert">
          {errorText}
        </p>
      </form>
    </div>
  );
}
